// File: src/pages/NotificationPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    TextField,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Box
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';

export default function NotificationPage() {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [filterText, setFilterText] = useState('');
    const [filterMode, setFilterMode] = useState('unread'); // "unread" or "all"
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Function to fetch notifications from the API.
    async function fetchNotifications() {
        try {
            const query = new URLSearchParams({
                filter: filterText,
                status: filterMode, // pass "unread" or "all" to your backend
                page: (page + 1).toString(),
                pageSize: rowsPerPage.toString()
            });
            const response = await fetch(`/api/notifications?${query.toString()}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                // Expecting data to have { notifications: [], total: number }
                setNotifications(data.notifications || []);
                setTotalCount(data.total || 0);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    }

    useEffect(() => {
        fetchNotifications();
    }, [filterText, filterMode, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const markAsRead = async (notificationId) => {
        try {
            const response = await fetch(`/api/notifications/${notificationId}/markasread`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                // Refresh notifications after marking as read.
                fetchNotifications();
            }
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    // Calls backend to mark all as read, then dispatches an event to clear in header.
    const clearAll = async () => {
        try {
            const response = await fetch(`/api/notifications/markallasread`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                // Dispatch an event to clear notifications in both header and page.
                document.dispatchEvent(new CustomEvent("clearNotifications"));
                fetchNotifications();
            }
        } catch (error) {
            console.error("Error clearing notifications:", error);
        }
    };

    const handleNotificationClick = (notification) => {
        // If unread, mark as read, then navigate if a link is provided.
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
        }
    };

    return (
        <PageLayout
            breadcrumb={[
                { label: 'Home', to: '/' },
                { label: 'Notifications' }
            ]}
            title="Notifications"
            subTitle="Manage your notifications"
            showClose={true}
            onClose={() => navigate('/')}
            icon={<NotificationImportantIcon sx={{ fontSize: 48 }} />}
        >
            {/* Top row: Clear All button + RadioGroup (Unread / All) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Button variant="outlined" onClick={clearAll}>
                    Clear All
                </Button>
                <RadioGroup
                    row
                    name="notificationStatus"
                    value={filterMode}
                    onChange={(e) => {
                        setFilterMode(e.target.value);
                        setPage(0);
                    }}
                >
                    <FormControlLabel value="unread" control={<Radio />} label="Unread" />
                    <FormControlLabel value="all" control={<Radio />} label="All" />
                </RadioGroup>
            </Box>

            {/* Next row: filter text field */}
            <TextField
                label="Filter Notifications"
                variant="outlined"
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setPage(0); }}
                fullWidth
                margin="normal"
            />

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell>Event Type</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>TimeStamp</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifications.map((n) => (
                            <TableRow
                                key={n.id}
                                hover
                                onClick={() => handleNotificationClick(n)}
                                style={{ cursor: 'pointer' }}
                            >
                                <TableCell>{n.id}</TableCell>
                                <TableCell>{n.message}</TableCell>
                                <TableCell>{n.eventType}</TableCell>
                                <TableCell>{n.severity}</TableCell>
                                <TableCell>{new Date(n.timeStamp).toLocaleString()}</TableCell>
                                <TableCell>
                                    {!n.read && (
                                        <Button
                                            variant="contained"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsRead(n.id);
                                            }}
                                        >
                                            Mark as Read
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {notifications.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No notifications found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>
        </PageLayout>
    );
}
